import type { UnitCategory } from "verdi-common/Configurations/SensorConfigurations/SensorConfigurationTypes";
import { getUnitCategoryConfiguration } from "verdi-common/Configurations/Units/UnitCategory/UnitCategoryConfigurations";
import {
    AxisSide,
    type AxisSpec,
    type BandSpec,
    PanelType,
    type StreamEntry,
    type TimeAxisPanelSpec,
} from "../ChartSpec";
import type { ResolvedStream } from "./resolve-streams";
import { countSeriesColor, nextSeriesColor } from "./stream-style";
import { type UnitCategoryPresentation, unitCategoryColor, unitCategoryRank } from "./unit-category-presentation";

/** Axis ids are the unit category. The generator and the split/merge transforms both count on that. */
function categoryAxis(unitCategory: string, side: AxisSide): AxisSpec {
    return { id: unitCategory, unitCategory, side };
}

/** One category, or several on one axis, which takes the first one's units. */
export type RecipeAxis = UnitCategory | UnitCategory[];

/**
 * The unit categories that go on one panel: an axis per entry in `left`, and at most one on the
 * right. A category belongs to at most one recipe, or it would be drawn on two panels.
 */
export type PanelRecipe = { left: RecipeAxis[]; right?: RecipeAxis };

function axisCategories(axis: RecipeAxis): UnitCategory[] {
    return Array.isArray(axis) ? axis : [axis];
}

function recipeCategories(recipe: PanelRecipe): string[] {
    return [...recipe.left, ...(recipe.right ? [recipe.right] : [])].flatMap(axisCategories);
}

/** Unit categories in the order their first stream appears. */
function categoriesOf(streams: StreamEntry[]): string[] {
    return [...new Set(streams.map((stream) => stream.axisId))];
}

/** Same object back when the axis already matches, so untouched entries keep their identity. */
export function withAxisId(stream: StreamEntry, axisId: string): StreamEntry {
    return stream.axisId === axisId ? stream : { ...stream, axisId };
}

/** The recipe's axes that have streams. A right axis with nothing on the left moves to the left. */
function buildRecipePanel(streams: StreamEntry[], recipe: PanelRecipe): TimeAxisPanelSpec {
    const streamsOn = (axis: RecipeAxis) =>
        axisCategories(axis).flatMap((category) => streams.filter((stream) => stream.axisId === category));

    const left = recipe.left.map(streamsOn).filter((axisStreams) => axisStreams.length > 0);
    const right = recipe.right ? streamsOn(recipe.right) : [];
    const sides: [AxisSide, StreamEntry[]][] = left.map((axisStreams) => [AxisSide.Left, axisStreams]);
    if (right.length > 0) sides.push([left.length > 0 ? AxisSide.Right : AxisSide.Left, right]);

    const axes = sides.map(([side, axisStreams]) => categoryAxis(axisStreams[0].axisId, side));

    return {
        id: `recipe-panel-${axes.map((axis) => axis.id).join("-")}`,
        type: PanelType.TimeAxis,
        axes,
        streams: sides.flatMap(([, axisStreams]) =>
            axisStreams.map((stream) => withAxisId(stream, axisStreams[0].axisId)),
        ),
    };
}

export function buildCategoryPanel(unitCategory: string, streams: StreamEntry[]): TimeAxisPanelSpec {
    return {
        id: `category-panel-${unitCategory}`,
        type: PanelType.TimeAxis,
        axes: [categoryAxis(unitCategory, AxisSide.Left)],
        streams: streams.map((stream) => withAxisId(stream, unitCategory)),
    };
}

/** One panel per unit category, in order of first appearance. */
export function buildCategoryPanels(streams: StreamEntry[]): TimeAxisPanelSpec[] {
    return categoriesOf(streams).map((unitCategory) =>
        buildCategoryPanel(
            unitCategory,
            streams.filter((stream) => stream.axisId === unitCategory),
        ),
    );
}

/**
 * A panel per recipe with streams in at least two of its categories, and a category panel for
 * every leftover, each sitting where its first category falls in the stream order. A recipe with
 * one category present leaves it on a category panel.
 */
export function buildRecipePanels(streams: StreamEntry[], recipes: PanelRecipe[]): TimeAxisPanelSpec[] {
    const present = new Set(streams.map((stream) => stream.axisId));
    const streamsIn = (unitCategory: string) => streams.filter((stream) => stream.axisId === unitCategory);

    const matched = recipes.filter(
        (recipe) => recipeCategories(recipe).filter((category) => present.has(category)).length > 1,
    );
    const recipeByCategory = new Map(
        matched.flatMap((recipe) => recipeCategories(recipe).map((category) => [category, recipe] as const)),
    );

    const built = new Set<PanelRecipe>();

    return categoriesOf(streams).flatMap((unitCategory) => {
        const recipe = recipeByCategory.get(unitCategory);
        if (!recipe) return buildCategoryPanel(unitCategory, streamsIn(unitCategory));
        if (built.has(recipe)) return [];

        built.add(recipe);
        return buildRecipePanel(streams, recipe);
    });
}

/** All streams on one panel, grouped by unit category with one left axis each. Exercises left y-axis selection. */
export function buildAllStreamsPanel(streams: StreamEntry[]): TimeAxisPanelSpec | null {
    if (streams.length === 0) return null;

    const unitCategories = categoriesOf(streams);

    return {
        id: "all-streams-panel",
        type: PanelType.TimeAxis,
        axes: unitCategories.map((unitCategory) => categoryAxis(unitCategory, AxisSide.Left)),
        streams: unitCategories.flatMap((unitCategory) => streams.filter((stream) => stream.axisId === unitCategory)),
    };
}

export function irrigationBand(
    zoneId: string,
    { position, plotHighlight }: Pick<BandSpec, "position" | "plotHighlight"> = {},
): BandSpec {
    return {
        kind: "scheduled-and-actual",
        scheduled: { kind: "legacy-zone-history", zoneId, signal: "scheduled" },
        actual: { kind: "legacy-zone-history", zoneId, signal: "valve-open" },
        ...(position && { position }),
        ...(plotHighlight && { plotHighlight }),
    };
}

/**
 * Panels and streams in the order the card's presentation list puts them, the categories it says
 * nothing about last.
 *
 * Colors from the list are counted first, so the streams it leaves out step around them; those
 * then take the palette in the order the streams arrived, before sorting, so appending a stream
 * never repaints the others.
 *
 * @see unitCategoryRank
 * @see nextSeriesColor
 */
export function toStreamEntries(streams: ResolvedStream[], presentation: UnitCategoryPresentation[]): StreamEntry[] {
    const seenInCategory = new Map<string, number>();

    const listedColors = streams.map((stream) => {
        const indexInCategory = seenInCategory.get(stream.unitCategory) ?? 0;
        seenInCategory.set(stream.unitCategory, indexInCategory + 1);

        return unitCategoryColor(presentation, stream.unitCategory, indexInCategory);
    });

    const useCounts = new Map<string, number>();
    listedColors.forEach((color) => {
        if (color) countSeriesColor(useCounts, color);
    });

    return streams
        .map((stream, index) => toStreamEntry(stream, listedColors[index] ?? nextSeriesColor(useCounts)))
        .sort(
            (a, b) => unitCategoryRank(presentation, a.unitCategory) - unitCategoryRank(presentation, b.unitCategory),
        );
}

export function toStreamEntry(stream: ResolvedStream, color: string): StreamEntry {
    return {
        streamId: stream.id,
        color,
        name: stream.name,
        axisId: stream.unitCategory,
        unitCategory: stream.unitCategory,
        aggregate: stream.aggregate,
        ...(stream.sensorClusterName ? { sensorClusterName: stream.sensorClusterName } : {}),
        ...(stream.sensorElementName ? { sensorElementName: stream.sensorElementName } : {}),
        zoneNames: stream.zoneNames,
        ...(stream.cardRelation ? { cardRelation: stream.cardRelation } : {}),
        seriesType: getUnitCategoryConfiguration(stream.unitCategory)?.preferredSeriesType ?? "line",
    };
}
